import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { getDeviceId } from '../utils/deviceId';

export default function MFAVerifyPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { username, maskedEmail, device_id, mfa_type } = location.state || {};

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isTOTP = mfa_type === 'totp';

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const deviceId = device_id || await getDeviceId();
      const { data } = await api.post('/auth/mfa-verify/', {
        username,
        otp,
        device_id: deviceId,
        device_name: localStorage.getItem('device_name') || 'Unknown Device',
        mfa_type: mfa_type || 'email',
      });
      login(data.user, { access: data.access, refresh: data.refresh });
      if (data.must_change_password) {
        navigate('/first-login');
      } else {
        navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', background: '#f4f6f4',
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ background: '#1a4d2e', borderRadius: '12px 12px 0 0', p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 48, height: 48, background: '#2d7a4f', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: 18 }}>
              Two-Factor Authentication
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
              {isTOTP ? 'Google Authenticator' : 'Email OTP'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ background: 'white', borderRadius: '0 0 12px 12px', border: '0.5px solid #e0e8e0', borderTop: 'none', p: 3 }}>
          <Typography sx={{ fontSize: 13, color: '#666', mb: 3 }}>
            {isTOTP
              ? 'Enter the 6-digit code from your Google Authenticator app.'
              : `An OTP has been sent to ${maskedEmail}. It is valid for 3 minutes.`
            }
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

          <form onSubmit={handleVerify}>
            <TextField
              label={isTOTP ? 'Authenticator code' : '6-digit OTP'}
              fullWidth
              required
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, style: { letterSpacing: '0.4em', fontSize: 22, textAlign: 'center' } }}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || otp.length !== 6}
              sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify'}
            </Button>
          </form>

          <Button
            fullWidth
            sx={{ mt: 1.5, color: '#888', fontSize: 13 }}
            onClick={() => navigate('/login')}
          >
            Back to login
          </Button>
        </Box>
      </Box>
    </Box>
  );
}